import { t } from '@lingui/core/macro'
import intersectionWith from 'lodash/intersectionWith'
import { integrationSlugs } from '@global/constants'
import { folderFilterIsValid } from './integrations/integration-forms/adobe-experience-manager-inbound/components'
import {
  validateTableName as validateSnowflakeTableName,
  ERROR_MESSAGES as SNOWFLAKE_ERROR_MESSAGES,
} from './integrations/integration-forms/snowflake-outbound/utils'
import {
  templateFieldHasCalculated,
  validateReference,
} from '../../../field-governance/calculated/utils'

// function for getting integration name string to make error messaging more descriptive
export const getIntegrationNameBySlug = slug => {
  switch (slug) {
    case 'adobe-experience-manager-inbound':
      return t`inbound Adobe Experience Manager`
    case 'adobe-experience-manager-outbound':
      return t`outbound Adobe Experience Manager`
    case 'adobe-inbound':
      return t`inbound Adobe`
    case 'adobe-outbound':
      return t`outbound Adobe`
    case 'amazon-ads-inbound':
      return t`Amazon Ads Inbound`
    case 'aws-s3-inbound':
      return t`AWS S3`
    case 'azure-blob-push':
      return t`Azure Blob`
    case 'bitly':
      return t`Bitly`
    case 'box-push':
      return t`Box Push`
    case 'box-push-general':
      return t`Box Push General`
    case 'email-output':
      return t`Email`
    case 'facebook-ads':
      return t`Facebook Ads`
    case 'facebook-ads-update':
      return t`Facebook Ads`
    case 'google-cloud':
      return t`Google Cloud`
    case 'google-dcm':
      return t`Google DCM`
    case 'google-dcm-output':
      return t`outbound Google DCM`
    case 'NOOP':
      return t`NOOP`
    case 'qr-code-monkey':
      return t`QRCode Monkey`
    case 's3-push':
      return t`S3 Push`
    case 'sftp-pull':
      return t`inbound SFTP`
    case 'sftp-push':
      return t`outbound SFTP`
    case 'workfront-project-pull-task-trigger':
      return t`Workfront`
    case 'branch':
      return t`Branch`
    case 'google-analytics':
      return t`Google Analytics`
    case 'google-ads-inbound':
      return t`Google Ads Inbound`
    case 'pinterest-inbound':
      return t`Pinterest Inbound`
    case 'snapchat-marketing-inbound':
      return t`Snapchat Inbound`
    default:
      return ''
  }
}

export const validateTemplate = (template, lists, fieldsets, patterns) => {
  const errors = []
  const warnings = []
  const usedLists = {}

  const fieldsById = Object.values(template.templateFieldsById)
    .map(item => item.field)
    .reduce((memo, item) => ({ ...memo, [item ? item.id : null]: item }), {})

  const templateFieldsByFieldId = Object.values(template.templateFieldsById).reduce(
    (memo, item) => ({ ...memo, [item.field_id]: item }),
    {},
  )

  if (!template.template.name) {
    errors.push({
      type: 'ERROR',
      message: t`Template Name is required.`,
      reference: { step: 'setup', id: 'templateName' },
    })
  }
  if (!template.template.folder_id) {
    errors.push({
      type: 'ERROR',
      message: t`Folder is required.`,
      reference: { step: 'setup', id: 'templateFolder' },
    })
  }

  const hasCreativeConfiguration = template?.template?.creativeConfiguration?.length
  const hasSourceUrlDefined = Object.values(template?.templateFieldsById).some(
    field => field.classification_parameter_type === 'SOURCE_URL',
  )
  const hasCreativeField = Object.values(template?.templateFieldsById).some(
    field => field?.governance_type === 'creative' || field?.field?.governance_type === 'creative',
  )

  if (
    template.template.isCreativeClassificationEnabled &&
    !hasCreativeConfiguration &&
    !hasCreativeField
  ) {
    errors.push({
      type: 'ERROR',
      message: t`At least one classification or Content Comprehension field is required to enable Content Comprehension.`,
      reference: { step: 'govern' },
    })
  }

  if (hasCreativeConfiguration && !hasSourceUrlDefined) {
    errors.push({
      type: 'ERROR',
      message: t`Setting an AI Classifications Source URL is required when using Creative Configuration.`,
      reference: { step: 'creative', id: 'sourceUrl' },
    })
  }

  Object.values(template.templateFieldsById).forEach(tf => {
    if (!tf.field) {
      if (tf.governance_type === 'pattern') {
        errors.push({
          type: 'ERROR',
          message: t`A Pattern was deleted.`,
          reference: { step: 'govern', id: `SCHEMA_ITEM_${tf.id}`, fieldId: tf.id },
        })
      } else {
        errors.push({
          type: 'ERROR',
          message: t`A field used in this template no longer exists.`,
          reference: { step: 'govern', id: `SCHEMA_ITEM_${tf.id}`, fieldId: tf.id },
        })
      }
    } else {
      const fieldName = tf.field.name
      if (tf.field.segment_parent_field_id && !patterns[tf.field.segment_parent_field_id]) {
        errors.push({
          type: 'ERROR',
          message: t`${fieldName}'s parent pattern has been deleted.`,
          reference: { step: 'govern', id: `SCHEMA_ITEM_${tf.id}`, fieldId: tf.id },
        })
      }
      if (
        !fieldsets[tf.field?.fieldset_id] &&
        !tf.field.is_pattern &&
        !tf.field?.segment_type &&
        tf.governance_type !== 'cv_id' &&
        tf.governance_type !== 'link' &&
        tf.governance_type !== 'shortened link' &&
        tf.governance_type !== 'qr-code' &&
        tf.governance_type !== 'deep-link'
      ) {
        errors.push({
          type: 'ERROR',
          message: t`${fieldName}'s parent field set has been deleted.`,
          reference: { step: 'govern', id: `SCHEMA_ITEM_${tf.id}`, fieldId: tf.id },
        })
      }
      if (!tf.governance_type && !tf.field.governance_type) {
        if (tf.field?.segment_type) {
          errors.push({
            type: 'ERROR',
            message: t`${fieldName} does not have an assigned type.`,
            reference: { step: 'govern', id: `SCHEMA_ITEM_${tf.id}`, fieldId: tf.id },
          })
        } else {
          errors.push({
            type: 'ERROR',
            message: t`${fieldName} does not have an assigned type in its parent field set`,
            reference: { step: 'govern', id: `SCHEMA_ITEM_${tf.id}`, fieldId: tf.id },
          })
        }
      }
      if (tf.governance_type === 'autofill') {
        if (!fieldsById[tf.autofill?.field_id]) {
          errors.push({
            type: 'ERROR',
            message: t`${fieldName} is trying to autofill from a field that does not exist in this template.`,
            reference: { step: 'govern', id: `SCHEMA_ITEM_${tf.id}`, fieldId: tf.id },
          })
        } else if (tf.required || tf.field.required) {
          if (
            !fieldsById[tf.autofill?.field_id].required &&
            !templateFieldsByFieldId[tf.autofill?.field_id].required
          ) {
            errors.push({
              type: 'ERROR',
              message: t`${fieldName} is required and is autofilling from a field that is not marked as required.`,
              reference: { step: 'govern', id: `SCHEMA_ITEM_${tf.id}`, fieldId: tf.id },
            })
          }
        }
      }
      if (tf.field.governance_type === 'autofill') {
        if (!fieldsById[tf.field.autofill?.field_id]) {
          errors.push({
            type: 'ERROR',
            message: t`${fieldName} is trying to autofill from a field that does not exist in this template.`,
            reference: { step: 'govern', id: `SCHEMA_ITEM_${tf.id}`, fieldId: tf.id },
          })
        } else if (tf.required || tf.field.required) {
          if (
            !fieldsById[tf.field.autofill?.field_id].required &&
            !templateFieldsByFieldId[tf.field.autofill?.field_id].required
          ) {
            errors.push({
              type: 'ERROR',
              message: t`${fieldName} is required and is autofilling from a field that is not marked as required.`,
              reference: { step: 'govern', id: `SCHEMA_ITEM_${tf.id}`, fieldId: tf.id },
            })
          }
        }
      }
      if (tf.field.governance_type === 'text' && templateFieldHasCalculated(tf)) {
        const error = validateReference(tf, fieldsById)

        if (error) {
          errors.push(error)
        }
      }
      if (tf.field.governance_type === 'concatenation') {
        tf.field.concatenation_governance.concatenation_governance_patterns.forEach(cgp => {
          if (!fieldsById[cgp.concatenation_field_id]) {
            errors.push({
              type: 'ERROR',
              message: t`${fieldName} is trying to concatenate a field that does not exist in this template.`,
              reference: { step: 'govern', id: `SCHEMA_ITEM_${tf.id}`, fieldId: tf.id },
            })
          }
        })
      }
      if ((tf.field.required || tf.required) && !tf.visible_on_form) {
        // the field is marked as required and it's not visible.
        // text
        if (tf.governance_type === 'text' || tf.field.governance_type === 'text') {
          errors.push({
            type: 'ERROR',
            message: t`${fieldName} requires manual user input and it's not visible. Users will not be able to submit.`,
            reference: { step: 'govern', id: `SCHEMA_ITEM_${tf.id}`, fieldId: tf.id },
          })
        }
      }
      if (tf.field.is_pattern) {
        const fullPattern = patterns[tf.field.id]
        const patternIsOutdated = fullPattern?.segments?.reduce((memo, item) => {
          if (!fieldsById[item.id]) {
            return true
          }
          return memo
        }, false)

        if (patternIsOutdated) {
          errors.push({
            type: 'ERROR',
            message: t`The pattern (${fieldName}) has been updated.`,
            reference: { step: 'govern', id: `SCHEMA_ITEM_${tf.id}`, fieldId: tf.id },
            data: fullPattern,
          })
        }
      }
      if (tf.governance_type === 'list' || tf.field.governance_type === 'list') {
        const governanceOwner = tf.list_governance ? tf.list_governance : tf.field.list_governance
        const referenceError = validateReference(tf, fieldsById)

        if (referenceError) {
          errors.push(referenceError)
        }

        if (!usedLists[governanceOwner.lists?.id]) {
          usedLists[governanceOwner.lists?.id] = { [governanceOwner.list_column_id]: tf }
        } else {
          usedLists[governanceOwner.lists?.id] = {
            ...usedLists[governanceOwner.lists?.id],
            [governanceOwner.list_column_id]: tf,
          }
        }

        if (
          (tf.list_governance && !tf.list_governance.list_column_id) ||
          (tf.field.list_governance && !tf.field.list_governance.list_column_id)
        ) {
          errors.push({
            type: 'ERROR',
            message: t`${fieldName} is governed by a list column that no longer exists.`,
            reference: { step: 'govern', id: `SCHEMA_ITEM_${tf.id}`, fieldId: tf.id },
          })
        }
        if (
          (tf.list_governance && !tf.list_governance.lists) ||
          (tf.field.list_governance && !tf.field.list_governance.lists)
        ) {
          errors.push({
            type: 'ERROR',
            message: t`${fieldName} is governed by a list that no longer exists`,
            reference: { step: 'govern', id: `SCHEMA_ITEM_${tf.id}`, fieldId: tf.id },
          })
        }
      }

      if (tf.governance_type === 'creative' || tf.field.governance_type === 'creative') {
        if (!hasSourceUrlDefined) {
          errors.push({
            type: 'ERROR',
            message: t`Enabling Content Comprehension and setting a Source URL is required to add a Content Comprehension field.`,
            reference: { step: 'govern', id: `SCHEMA_ITEM_${tf.id}`, fieldId: tf.id },
          })
        }
      }
    }
  })

  Object.keys(usedLists).forEach(listId => {
    const fullList = lists[listId]
    const ourList = usedLists[listId]

    const fullListColumns = fullList
      ? fullList.list_columns.sort((a, b) => a.precedence - b.precedence).map(lc => lc.id)
      : []
    const ourListColumns = Object.keys(ourList)

    fullListColumns.forEach((flc, index) => {
      if (!ourListColumns.includes(`${flc}`)) {
        const fullListName = fullList.name
        warnings.push({
          type: 'WARNING',
          message: t`You are not using all columns of the ${fullListName} list`,
          reference: { step: 'govern', id: `NO_ID`, fieldId: null },
        })
        // now check to see if our list contains any columns that come after the missing column, in the full list array
        const dependentFullListColumns = fullListColumns.splice(index + 1, fullListColumns.length)
        const columnDifferences = intersectionWith(
          dependentFullListColumns,
          ourListColumns,
          (a, b) => a === b,
        )
        if (columnDifferences.length) {
          columnDifferences.forEach(cd => {
            const fieldName = ourList[cd].field.name
            warnings.push({
              type: 'WARNING',
              message: t`The field ${fieldName} is dependent on a column from the list (${fullListName}) which is not used in this template.`,
              reference: {
                step: 'govern',
                id: `SCHEMA_ITEM_${ourList[cd].id}`,
                fieldId: ourList[cd].id,
              },
            })
          })
        }
      } else if (!ourList[flc].visible_on_form && !ourList[flc].default_value) {
        // check if drop down. If it is that requires user input
        const listGovernance = ourList[flc].list_governance || ourList[flc].field.list_governance
        if (listGovernance && listGovernance.lists.type === 'drop-down') {
          const fieldName = ourList[flc].field.name
          warnings.push({
            type: 'WARNING',
            message: t`The field ${fieldName} requires the user to select a value from the list, it must be visible.`,
            reference: {
              step: 'govern',
              id: `SCHEMA_ITEM_${ourList[flc].id}`,
              fieldId: ourList[flc].id,
            },
          })
        }
      }
    })
  })

  if (template.integrations.length) {
    template.integrations.forEach(i => {
      switch (i.slug) {
        case integrationSlugs.QR_CODE_MONKEY:
          validateQRCodeMonkeyIntegration(i, template, error => {
            errors.push(error)
          })
          break
        case integrationSlugs.BRANCH:
          validateBranchIntegration(i, template, error => {
            errors.push(error)
          })
          break
        case integrationSlugs.FACEBOOK_ADS:
          validateFacebookAdsIntegration(i, error => {
            errors.push(error)
          })
          break
        case integrationSlugs.GOOGLE_DCM:
          validateGoogleDCMIntegration(i, error => {
            errors.push(error)
          })
          break
        case integrationSlugs.BITLY:
          validateBitlyIntegration(i, template, error => {
            errors.push(error)
          })
          break
        case integrationSlugs.S3_PUSH:
          validateOutboundS3Integration(i, template, error => {
            errors.push(error)
          })
          break
        case integrationSlugs.AWS_S3_INBOUND:
        case integrationSlugs.SFTP_PULL:
        case integrationSlugs.BOX_PUSH:
          validateGeneralIntegration(i, error => {
            errors.push(error)
          })
          break
        case integrationSlugs.GOOGLE_ANALYTICS:
          validateGoogleAnalyticsIntegration(i, error => {
            errors.push(error)
          })
          break
        case integrationSlugs.ADOBE_EXPERIENCE_MANAGER_INBOUND:
          validateAEMInboundIntegration(i, template, error => {
            errors.push(error)
          })
          break
        case integrationSlugs.ADOBE_EXPERIENCE_MANAGER_OUTBOUND:
          validateAEMOutboundIntegration(i, template, error => {
            errors.push(error)
          })
          break
        case integrationSlugs.GOOGLE_ADS_INBOUND:
          validateGoogleAdsInboundIntegration(i, error => {
            errors.push(error)
          })
          break
        case integrationSlugs.PINTEREST_INBOUND:
          validatePinterestInboundIntegration(i, error => {
            errors.push(error)
          })
          break
        case integrationSlugs.FACEBOOK_MARKETING_INBOUND:
          validateFacebookMarketingInbound(i, error => {
            errors.push(error)
          })
          break
        case integrationSlugs.SNAPCHAT_MARKETING_INBOUND:
          validateSnapChatInboundIntegration(i, error => {
            errors.push(error)
          })
          break
        case integrationSlugs.AMAZON_ADS:
          validateAmazonAdsInboundIntegration(i, error => {
            errors.push(error)
          })
          break
        case integrationSlugs.SNOWFLAKE_OUTBOUND:
          validateOutboundSnowflake(i, error => {
            errors.push(error)
          })
          break
        default:
          break
      }
    })
  }

  // TODO:
  // warnings:
  //* ***mapped fieldsets not associated with a pattern.
  // output configurations have extra/less fields than in the template

  // maybes
  // two fields with the same name
  // lists in variables. Discouraged.
  // lists out of order warning. Bad experience
  // parameters don't have a template field. Or value assigned.
  // link validations aren't fully configured?

  // probably should throw error state on every field when they have an error.
  // would need to keep all those in the reducer store, and then every field could check itself if it needs an error or not. Might cause more rendering?
  return { errors, warnings }
}

const validateLinkExists = (integration, template, sendError) => {
  const integrationName = getIntegrationNameBySlug(integration.slug)
  if (!Object.values(template.fieldsById).some(field => field.governance_type === 'link')) {
    sendError({
      type: 'ERROR',
      message: t`The integration ${integrationName} cannot be added without a link field`,
      reference: { step: 'connect', id: integration.id },
    })
  }
}

const validateNoEmptyFields = (integration, sendError) => {
  const integrationName = getIntegrationNameBySlug(integration.slug)
  integration.mapping_fields.forEach(mapping => {
    const fieldName = mapping.fieldName
    if (!mapping.fieldName) {
      sendError({
        type: 'ERROR',
        message: t`There is no field selected for one of your ${integrationName} mappings.`,
        reference: { step: 'connect', id: mapping.fieldName },
      })
    }
    if (!mapping.cvFieldId) {
      sendError({
        type: 'ERROR',
        message: t`There is no Claravine field selected for the ${integrationName} ${fieldName} mapping.`,
        reference: { step: 'connect', id: mapping.cvFieldId },
      })
    }
  })
}

const validateSimpleInboundIntegration = (integration, sendError) => {
  const integrationName = getIntegrationNameBySlug(integration.slug)
  validateGeneralIntegration(integration, sendError)
  if (!(Array.isArray(integration.mapping_fields) && integration.mapping_fields.length > 0)) {
    sendError({
      type: 'ERROR',
      message: t`A ${integrationName} integration must have at least one field mapping.`,
    })
  }
  if (integration.mapping_fields) {
    validateNoEmptyFields(integration, sendError)
  }
}

// ********** Integration Validators
// eventually add validate functions for all integrations that will deal with the specific fields and metadata required for each integration.

const validateGeneralIntegration = (integration, sendError) => {
  const integrationName = getIntegrationNameBySlug(integration.slug)
  if (!integration.connector_id) {
    sendError({
      type: 'ERROR',
      message: t`Select an account for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
}

const validateQRCodeMonkeyIntegration = (integration, template, sendError) => {
  validateLinkExists(integration, template, sendError)
}

const validateBitlyIntegration = (integration, template, sendError) => {
  const integrationName = getIntegrationNameBySlug(integration.slug)
  if (!integration.connector_id) {
    sendError({
      type: 'ERROR',
      message: t`Select an account for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }

  if (integration.metadata) {
    const { bitly_group_id, bitly_org_id, shortened_url } = integration.metadata
    const labeledData = {
      Group: bitly_group_id,
      Organization: bitly_org_id,
      'Branded Short Domain': shortened_url,
    }

    const renderErrorMessage = fieldName => {
      sendError({
        type: 'ERROR',
        message: t`The ${integrationName} integration is missing the required field "${fieldName}"`,
        reference: { step: 'connect', id: integration.id },
      })
    }

    for (const prop in labeledData) {
      if (!labeledData[prop]) renderErrorMessage(prop)
    }
  }

  if (Array.isArray(integration.mapping_fields)) {
    validateNoEmptyFields(integration, sendError)
  }
  validateLinkExists(integration, template, sendError)
}

const validateFacebookAdsIntegration = (integration, sendError) => {
  const integrationName = getIntegrationNameBySlug(integration.slug)
  if (!integration.connector_id) {
    sendError({
      type: 'ERROR',
      message: t`Select an account for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
  if (integration.metadata && !integration.new_mapping) {
    for (const [key, value] of Object.entries(integration.metadata)) {
      if (
        (key === 'ad_id' && !value) ||
        (key === 'generated_link' && !value) ||
        (key === 'creative_id' && !value)
      ) {
        sendError({
          type: 'ERROR',
          message: t`There is no Claravine field selected for the ${integrationName} ${key} mapping.`,
          reference: { step: 'connect', id: key },
        })
      }
    }
  }
  if (integration.mapping_fields && integration.new_mapping) {
    if (Array.isArray(integration.mapping_fields)) {
      validateNoEmptyFields(integration, sendError)
    } else {
      for (const [key, value] of Object.entries(integration.mapping_fields)) {
        if (!value) {
          sendError({
            type: 'ERROR',
            message: t`There is no Claravine field selected for the ${integrationName} ${key} mapping.`,
            reference: { step: 'connect', id: key },
          })
        }
      }
    }
  }
}

const validateGoogleDCMIntegration = (integration, sendError) => {
  const integrationName = getIntegrationNameBySlug(integration.slug)
  if (!integration.connector_id) {
    sendError({
      type: 'ERROR',
      message: t`Select an account for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
  if (integration.metadata && !integration.new_mapping) {
    for (const [key, value] of Object.entries(integration.metadata)) {
      if (
        (key === 'adId' && !value) ||
        (key === 'campaignId' && !value) ||
        (key === 'placementId' && !value) ||
        (key === 'campaign' && !value)
      ) {
        sendError({
          type: 'ERROR',
          message: t`There is no Claravine field selected for the ${integrationName} ${key} mapping.`,
          reference: { step: 'connect', id: key },
        })
      }
    }
  }
  if (integration.mapping_fields && integration.new_mapping) {
    if (Array.isArray(integration.mapping_fields)) {
      validateNoEmptyFields(integration, sendError)
    } else {
      for (const [key, value] of Object.entries(integration.mapping_fields)) {
        if (!value) {
          sendError({
            type: 'ERROR',
            message: t`There is no Claravine field selected for the ${integrationName} ${key} mapping.`,
            reference: { step: 'connect', id: key },
          })
        }
      }
    }
  }
}

const validateBranchIntegration = (integration, template, sendError) => {
  const integrationName = getIntegrationNameBySlug(integration.slug)
  if (!integration.connector_id) {
    sendError({
      type: 'ERROR',
      message: t`Select an account for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
  if (integration.mapping_fields) {
    if (Array.isArray(integration.mapping_fields)) {
      validateNoEmptyFields(integration, sendError)
    } else {
      for (const [key, value] of Object.entries(integration.mapping_fields)) {
        if (!value) {
          sendError({
            type: 'ERROR',
            message: t`There is no Claravine field selected for the ${integrationName} ${key} mapping.`,
            reference: { step: 'connect', id: key },
          })
        }
      }
    }
  }
  validateLinkExists(integration, template, sendError)
}

const validateIntegrationIsSetOnce = (integration, template, sendError) => {
  const integrationName = getIntegrationNameBySlug(integration.slug)
  const templateIntegrationsWithSameSlug = template.integrations.filter(
    ({ slug }) => slug === integration.slug,
  )
  const integrationIndex = templateIntegrationsWithSameSlug.findIndex(
    templateIntegration => integration === templateIntegration,
  )
  const integrationIsTheLastAdded = integrationIndex === templateIntegrationsWithSameSlug.length - 1
  if (templateIntegrationsWithSameSlug.length > 1 && integrationIsTheLastAdded) {
    sendError({
      type: 'ERROR',
      message: t`Templates are limited to a single ${integrationName} integration. Please remove all but one ${integrationName} integration.`,
      reference: { step: 'connect', id: integration.id },
    })
  }
}

const validateAEMInboundIntegration = (integration, template, sendError) => {
  const integrationName = getIntegrationNameBySlug(integration.slug)
  if (!integration.connector_id) {
    sendError({
      type: 'ERROR',
      message: t`Select an account for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
  if (!integration.metadata.aem_data_type) {
    sendError({
      type: 'ERROR',
      message: t`Select a data type to load for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
  if (!integration.name) {
    sendError({
      type: 'ERROR',
      message: t`Integration name is required for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
  if (integration.metadata.folderFilter) {
    if (!folderFilterIsValid(integration.metadata.folderFilter)) {
      sendError({
        type: 'ERROR',
        message: t`The folder filter must start with /`,
        reference: { step: 'connect', id: integration.metadata.folderFilter },
      })
    }
  }
  if (integration.mapping_fields) {
    if (Array.isArray(integration.mapping_fields)) {
      validateNoEmptyFields(integration, sendError)
    } else {
      for (const [key, value] of Object.entries(integration.mapping_fields)) {
        if (!value) {
          sendError({
            type: 'ERROR',
            message: t`There is no Claravine field selected for the ${integrationName} ${key} mapping.`,
            reference: { step: 'connect', id: key },
          })
        }
      }
    }
  }
}

const validateGoogleAdsInboundIntegration = (integration, sendError) => {
  const integrationName = getIntegrationNameBySlug(integration.slug)
  if (!integration.connector_id) {
    sendError({
      type: 'ERROR',
      message: t`Select an account for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
  if (!integration.metadata.ga_data_type) {
    sendError({
      type: 'ERROR',
      message: t`Select a data type to load for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
  if (!integration.name) {
    sendError({
      type: 'ERROR',
      message: t`Integration name is required for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
  if (integration.filters) {
    integration.filters.forEach(filter => {
      if (!filter.values.length) {
        sendError({
          type: 'ERROR',
          message: t`There is no value for one of your ${integrationName} filters.`,
          reference: { step: 'connect', id: filter.integrationFilterId },
        })
      }
      filter.values.forEach(value => {
        if (value.value.trim() === '') {
          sendError({
            type: 'ERROR',
            message: t`There is no value for one of your ${integrationName} filters.`,
            reference: { step: 'connect', id: filter.integrationFilterId },
          })
        }
      })
    })
  }
  if (integration.mapping_fields) {
    if (Array.isArray(integration.mapping_fields)) {
      validateNoEmptyFields(integration, sendError)
    } else {
      for (const [key, value] of Object.entries(integration.mapping_fields)) {
        if (!value) {
          sendError({
            type: 'ERROR',
            message: t`There is no Claravine field selected for the ${integrationName} ${key} mapping.`,
            reference: { step: 'connect', id: key },
          })
        }
      }
    }
  }
}

const validatePinterestInboundIntegration = (integration, sendError) => {
  validateSimpleInboundIntegration(integration, sendError)
}

const validateFacebookMarketingInbound = (integration, sendError) => {
  validateSimpleInboundIntegration(integration, sendError)
}

const validateSnapChatInboundIntegration = (integration, sendError) => {
  validateSimpleInboundIntegration(integration, sendError)
}

const validateAmazonAdsInboundIntegration = (integration, sendError) => {
  validateSimpleInboundIntegration(integration, sendError)
}

const validateAEMOutboundIntegration = (integration, template, sendError) => {
  const integrationName = getIntegrationNameBySlug(integration.slug)
  const AEMInboundIntegrations = template.integrations.filter(
    ({ slug }) => slug === integrationSlugs.ADOBE_EXPERIENCE_MANAGER_INBOUND,
  )
  if (AEMInboundIntegrations.length < 1) {
    ;({
      type: 'ERROR',
      message: t`An ${integrationName} integration must also have an inbound integration of the same type. Please add and configure an inbound integration.`,
      reference: { step: 'connect', id: integration.id },
    })
  }
  validateIntegrationIsSetOnce(integration, template, sendError)
}

const validateGoogleAnalyticsIntegration = (integration, sendError) => {
  const integrationName = getIntegrationNameBySlug(integration.slug)
  if (integration.name.trim() === '') {
    sendError({
      type: 'ERROR',
      message: t`Add a name for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
  if (!integration.connector_id) {
    sendError({
      type: 'ERROR',
      message: t`Select a connected account for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
  if (!integration.metadata.ga_account_id) {
    sendError({
      type: 'ERROR',
      message: t`Select a Google Analytics Account for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
  if (!integration.metadata.ga_property_id) {
    sendError({
      type: 'ERROR',
      message: t`Select a GA Property for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
  if (!integration.metadata.ga_data_set_type) {
    sendError({
      type: 'ERROR',
      message: t`Select a Data Set Type for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
  if (!integration.metadata.ga_custom_data_source_id) {
    sendError({
      type: 'ERROR',
      message: t`Select a Data Source for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
  if (integration.mapping_fields) {
    if (Array.isArray(integration.mapping_fields)) {
      if (integration.mapping_fields.length === 0) {
        sendError({
          type: 'ERROR',
          message: t`The ${integrationName} integration requires field mappings before saving. Add your Data Set Schema to get started.`,
          reference: { step: 'connect', id: integration.id },
        })
      }
      validateNoEmptyFields(integration, sendError)
    } else {
      if (Object.keys(integration.mapping_fields) === 0) {
        sendError({
          type: 'ERROR',
          message: t`The ${integrationName} integration requires field mappings before saving. Add your Data Set Schema to get started.`,
          reference: { step: 'connect', id: integration.id },
        })
      }
      for (const [key, value] of Object.entries(integration.mapping_fields)) {
        if (!value) {
          sendError({
            type: 'ERROR',
            message: t`There is no Claravine field selected for the ${integrationName} ${key} mapping.`,
            reference: { step: 'connect', id: key },
          })
        }
      }
    }
  }
}

const validateOutboundS3Integration = (integration, template, sendError) => {
  const integrationName = getIntegrationNameBySlug(integration.slug)
  const integrationNotification = template.integrationNotifications[integration.id]
  const { emailOnFailure, emailOnSuccess, emailTo } = integrationNotification || {}
  if (integrationNotification && (emailOnFailure || emailOnSuccess) && emailTo.length < 1) {
    sendError({
      type: 'ERROR',
      message: t`At least one recipient e-mail address is required for the ${integrationName} e-mail alert.`,
      reference: { step: 'connect', id: integration.id },
    })
  }
}

const validateOutboundSnowflake = (integration, sendError) => {
  const integrationName = getIntegrationNameBySlug(integration.slug)
  const { tableName } = integration.metadata

  const { errors } = validateSnowflakeTableName(tableName)

  if (errors.length > 0) {
    errors.forEach(error => {
      const message = SNOWFLAKE_ERROR_MESSAGES[error.type]

      sendError({
        type: 'ERROR',
        message: t`Snowflake Outbound: ${message}`,
        reference: { step: 'connect', id: integration.id },
      })
    })
  }

  if (!integration.connector_id) {
    sendError({
      type: 'ERROR',
      message: t`Select a connected account for your ${integrationName} integration`,
      reference: { step: 'connect', id: integration.id },
    })
  }
}
